import {
    Search, Plus, CircleHelp, Settings, Bell, ChevronDown, Building2,
    Eye, Zap, Users, Target, IndianRupee, Sparkles, Trophy, TrendingUp,
    Wand2, LayoutTemplate, ListChecks, ArrowUpRight, ArrowDownRight, Minus,
} from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";

const TABS = ["Home", "Leads", "Accounts", "Contacts", "Opportunities", "Engage", "Field Sales", "Tasks", "Revenue", "Reports", "More"];

export const AppChrome = ({ active = "Home" }) => (
    <>
        {/* App top bar */}
        <div className="flex items-center gap-3 border-b border-[#EDF0F3] bg-white px-3.5 py-2.5">
            <span className="flex shrink-0 items-center">
                <img src="/assets/kryvo-logo.png" alt="Kryvo AI CRM" className="h-6 w-auto object-contain" />
            </span>
            <div className="mx-auto flex w-full max-w-[210px] items-center gap-2 rounded-lg border border-[#E5E7EB] bg-[#F8FAFC] px-2.5 py-1.5 text-[10px] text-[#94A3B8]">
                <Search className="h-3 w-3 shrink-0" />
                <span className="flex-1 truncate">Search…</span>
                <kbd className="hidden rounded border border-[#E5E7EB] bg-white px-1 text-[8px] font-semibold sm:block">⌘K</kbd>
            </div>
            <div className="ml-auto flex shrink-0 items-center gap-2.5">
                <span className="hidden items-center gap-1.5 text-[10px] font-semibold text-[#334155] md:flex">
                    <Building2 className="h-3 w-3 text-[#64748B]" />
                    Kryvo Technologies
                    <span className="rounded border border-[#E5E7EB] bg-[#F1F5F9] px-1 py-px text-[7px] font-bold text-[#64748B]">PROD</span>
                    <ChevronDown className="h-2.5 w-2.5 text-[#94A3B8]" />
                </span>
                <Plus className="hidden h-3.5 w-3.5 text-[#64748B] sm:block" />
                <CircleHelp className="hidden h-3.5 w-3.5 text-[#64748B] sm:block" />
                <Settings className="hidden h-3.5 w-3.5 text-[#64748B] md:block" />
                <span className="relative">
                    <Bell className="h-3.5 w-3.5 text-[#64748B]" />
                    <span className="absolute -right-1 -top-1 flex h-2.5 w-2.5 items-center justify-center rounded-full bg-red-500 text-[6px] font-bold text-white">3</span>
                </span>
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-[#16A34A] text-[9px] font-bold text-white">S</span>
            </div>
        </div>

        {/* Module tabs */}
        <div className="flex items-center gap-0.5 overflow-x-auto border-b border-[#EDF0F3] bg-white px-2.5 [scrollbar-width:none]">
            {TABS.map((t) => (
                <span
                    key={t}
                    className={`flex shrink-0 items-center gap-1 border-b-2 px-2.5 py-2 text-[10px] font-semibold ${
                        t === active
                            ? "border-[#16A34A] text-[#15803D]"
                            : "border-transparent text-[#64748B]"
                    }`}
                >
                    {t}
                    <ChevronDown className="h-2.5 w-2.5 opacity-60" />
                </span>
            ))}
        </div>
    </>
);

const kpis = [
    { label: "Total Leads", value: "2,540", icon: Users, sub: { type: "up", text: "12.4% vs last week" } },
    { label: "Open Opportunities", value: "120", icon: Target, sub: { type: "up", text: "8 new this week" } },
    { label: "Open Pipeline", value: "₹84.5L", icon: IndianRupee, sub: { type: "flat", text: "₹57.8L closed won" } },
    { label: "Forecast", value: "₹44.1L", icon: Sparkles, sub: { type: "flat", text: "62% confidence · Medium" } },
    { label: "Win Rate", value: "75%", icon: Trophy, sub: { type: "up", text: "75% vs prior 90d" } },
];

const stageData = [
    { name: "New", value: 19, color: "#3B82F6" },
    { name: "Qualified", value: 12, color: "#16A34A" },
    { name: "Proposal", value: 8, color: "#F59E0B" },
    { name: "Negotiation", value: 5, color: "#8B5CF6" },
    { name: "Won", value: 21, color: "#0EA5E9" },
];

const closureData = [
    { name: "This week", value: 11, color: "#16A34A" },
    { name: "This month", value: 18, color: "#3B82F6" },
    { name: "Next month", value: 9, color: "#F59E0B" },
    { name: "Later", value: 6, color: "#94A3B8" },
];

const focusItems = [
    { text: "Call Meridian Estates — proposal follow-up", time: "10:30", hot: true },
    { text: "Site visit · Skyline Group, Whitefield", time: "12:00", hot: false },
    { text: "Send quotation to FinEdge Capital", time: "14:15", hot: false },
    { text: "Demo · Orbit Staffing (12 seats)", time: "16:30", hot: true },
];

const KpiSub = ({ sub }) => {
    if (sub.type === "up")
        return <p className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold text-[#16A34A]"><ArrowUpRight className="h-3 w-3" />{sub.text}</p>;
    if (sub.type === "down")
        return <p className="mt-1.5 flex items-center gap-1 text-[10px] font-semibold text-red-500"><ArrowDownRight className="h-3 w-3" />{sub.text}</p>;
    return <p className="mt-1.5 flex items-center gap-1 text-[10px] font-medium text-[#94A3B8]"><Minus className="h-3 w-3" />{sub.text}</p>;
};

export const DashboardContent = () => (
    <div className="bg-[#F7F9FA] p-3.5 sm:p-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
                <p className="font-display text-[15px] font-extrabold text-[#111827]">Dashboard</p>
                <p className="text-[10px] text-[#64748B]">Your AI-powered sales command center</p>
            </div>
            <div className="flex rounded-lg border border-[#E5E7EB] bg-white p-0.5">
                <span className="flex items-center gap-1 rounded-md bg-[#16A34A] px-2.5 py-1 text-[9px] font-bold text-white">
                    <Eye className="h-2.5 w-2.5" /> Overview
                </span>
                <span className="flex items-center gap-1 px-2.5 py-1 text-[9px] font-semibold text-[#64748B]">
                    <Zap className="h-2.5 w-2.5" /> Focus Today
                </span>
            </div>
        </div>

        {/* KPI row */}
        <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-5">
            {kpis.map((k) => (
                <div key={k.label} className="rounded-xl border border-[#EDF0F3] bg-white p-3 shadow-sm">
                    <div className="flex items-start justify-between">
                        <p className="text-[8px] font-bold uppercase tracking-[0.1em] text-[#94A3B8]">{k.label}</p>
                        <span className="flex h-5 w-5 items-center justify-center rounded-md bg-[#F0FDF4]">
                            <k.icon className="h-2.5 w-2.5 text-[#16A34A]" />
                        </span>
                    </div>
                    <p className="mt-1 font-display text-lg font-extrabold leading-none text-[#111827]">{k.value}</p>
                    <KpiSub sub={k.sub} />
                </div>
            ))}
        </div>

        {/* Widget toolbar */}
        <div className="mt-3.5 flex flex-wrap items-center gap-2">
            <div className="mr-auto">
                <p className="text-[12px] font-bold text-[#111827]">Your Dashboard</p>
                <p className="text-[9px] text-[#94A3B8]">Adapts to the widgets you use most.</p>
            </div>
            <span className="hidden items-center gap-1 rounded-lg border border-[#E5E7EB] bg-white px-2 py-1 text-[9px] font-semibold text-[#475569] sm:flex">
                <Wand2 className="h-2.5 w-2.5 text-[#16A34A]" /> Auto Arrange
            </span>
            <span className="hidden items-center gap-1 rounded-lg border border-[#E5E7EB] bg-white px-2 py-1 text-[9px] font-semibold text-[#475569] md:flex">
                <LayoutTemplate className="h-2.5 w-2.5 text-[#64748B]" /> Templates
            </span>
            <span className="flex items-center gap-1 rounded-lg bg-[#16A34A] px-2 py-1 text-[9px] font-bold text-white">
                <Plus className="h-2.5 w-2.5" /> Add Widget
            </span>
        </div>

        {/* Widgets */}
        <div className="mt-2.5 grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
            <div className="rounded-xl border border-[#EDF0F3] bg-white p-3 shadow-sm">
                <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-[#111827]">Opportunities by Stage</p>
                    <span className="rounded bg-[#F1F5F9] px-1.5 py-0.5 text-[8px] font-semibold text-[#64748B]">65 total</span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                    <div className="h-[92px] w-[46%]">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={stageData} dataKey="value" innerRadius={0} outerRadius={42} strokeWidth={2} stroke="#fff">
                                    {stageData.map((s) => <Cell key={s.name} fill={s.color} />)}
                                </Pie>
                                <Tooltip contentStyle={{ fontSize: 10, borderRadius: 8, border: "1px solid #E5E7EB" }} />
                            </PieChart>
                        </ResponsiveContainer>
                    </div>
                    <div className="grid flex-1 gap-1">
                        {stageData.map((s) => (
                            <div key={s.name} className="flex items-center gap-1.5 text-[8px] font-medium text-[#64748B]">
                                <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />
                                <span className="flex-1">{s.name}</span>
                                <span className="font-bold text-[#111827]">{s.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="rounded-xl border border-[#EDF0F3] bg-white p-3 shadow-sm">
                <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-[#111827]">Expected Closure</p>
                    <span className="rounded bg-[#F1F5F9] px-1.5 py-0.5 text-[8px] font-semibold text-[#64748B]">44 deals</span>
                </div>
                <div className="mt-1 flex items-center gap-2">
                    <div className="relative h-[92px] w-[46%]">
                        <ResponsiveContainer width="100%" height="100%">
                            <PieChart>
                                <Pie data={closureData} dataKey="value" innerRadius={26} outerRadius={42} strokeWidth={2} stroke="#fff">
                                    {closureData.map((s) => <Cell key={s.name} fill={s.color} />)}
                                </Pie>
                                <Tooltip contentStyle={{ fontSize: 10, borderRadius: 8, border: "1px solid #E5E7EB" }} />
                            </PieChart>
                        </ResponsiveContainer>
                        <span className="pointer-events-none absolute inset-0 flex items-center justify-center text-[10px] font-extrabold text-[#111827]">₹44L</span>
                    </div>
                    <div className="grid flex-1 gap-1">
                        {closureData.map((s) => (
                            <div key={s.name} className="flex items-center gap-1.5 text-[8px] font-medium text-[#64748B]">
                                <span className="h-1.5 w-1.5 rounded-full" style={{ background: s.color }} />
                                <span className="flex-1">{s.name}</span>
                                <span className="font-bold text-[#111827]">{s.value}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            <div className="rounded-xl border border-[#EDF0F3] bg-white p-3 shadow-sm sm:col-span-2 lg:col-span-1">
                <div className="flex items-center justify-between">
                    <p className="text-[10px] font-bold text-[#111827]">Focus Today</p>
                    <span className="flex items-center gap-0.5 text-[8px] font-bold text-[#16A34A]">
                        <TrendingUp className="h-2.5 w-2.5" /> 4 due
                    </span>
                </div>
                <div className="mt-2 grid gap-1.5">
                    {focusItems.map((f) => (
                        <div key={f.text} className="flex items-center gap-2 rounded-lg bg-[#F8FAFC] px-2 py-1.5">
                            <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${f.hot ? "bg-[#16A34A]" : "bg-[#CBD5E1]"}`} />
                            <span className="min-w-0 flex-1 truncate text-[9px] font-medium text-[#334155]">{f.text}</span>
                            <span className="shrink-0 text-[8px] font-bold text-[#94A3B8]">{f.time}</span>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    </div>
);

const DashboardMockup = () => (
    <div
        data-testid="hero-dashboard-mockup-container"
        className="relative overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white shadow-2xl shadow-slate-900/10"
    >
        <AppChrome active="Home" />
        <DashboardContent />

        {/* Bottom utility pills */}
        <div className="pointer-events-none absolute bottom-2.5 left-2.5 flex items-center gap-1.5 rounded-full border border-[#E5E7EB] bg-white px-2.5 py-1 text-[9px] font-bold text-[#15803D] shadow-md">
            <ListChecks className="h-3 w-3" /> To Do List
            <span className="flex h-3.5 w-3.5 items-center justify-center rounded-full bg-[#16A34A] text-[7px] text-white">10</span>
        </div>
        <div className="pointer-events-none absolute bottom-2.5 right-2.5 flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-[#16A34A] to-[#15803D] shadow-lg shadow-[#16A34A]/30">
            <Sparkles className="h-3.5 w-3.5 text-white" />
        </div>
    </div>
);

export default DashboardMockup;
